import { CALL_OUTCOME_SLUGS } from './callOutcomeAnalytics';
import { isAiConfigured } from '../services/aiService';
import { inferCallOutcomeWithAi } from '../services/aiFeatures';

const SLUG_SET = new Set(CALL_OUTCOME_SLUGS);

function isGatekeeperContext(l) {
  if (/\bgatekeeper\b|\bassistenz\b|\bsekretär\b|\bsekretariat\b|\bvorzimmer\b|\bempfang\b|\brezeption\b/.test(l)) {
    return true;
  }
  if (/\b(noch|gerade|aktuell|derzeit)\s+(in|im)\s+(einem\s+)?(termin|meeting|besprechung)\b/.test(l)) return true;
  if (/\bist\s+(noch\s+)?(in\s+)?(termin|meeting|besprechung)\b/.test(l)) return true;
  if (/\b(termin|meeting|besprechung)\b.*\bbis\s+(\d{1,2}([:.]\d{2})?\s*(uhr)?|\d{1,2}\s*uhr)/.test(l)) return true;
  if (/\bbis\s+(\d{1,2}([:.]\d{2})?\s*(uhr)?|\d{1,2}\s*uhr)\b.*\b(termin|meeting|besprechung)\b/.test(l)) return true;
  if (/\b(später|nachher|gleich|nochmal|wieder)\b/.test(l) && /\b(melden|zurückmelden|rückmelden|anrufen|zurückrufen|sich\s+melden|mich\s+melden)\b/.test(l)) {
    return true;
  }
  if (/\b(muss|soll|wird)\s+(sich\s+|mich\s+)?(später|nachher|nochmal|wieder)\b/.test(l) && /\b(melden|anrufen|rück)\b/.test(l)) {
    return true;
  }
  if (/\bum\s+\d{1,2}([:.]\d{2})?\s*(uhr)?\b/.test(l) && /\b(melden|anrufen|rück|zurück)\b/.test(l) && !/\b(termin\s+)?(vereinbart|gesetzt|fixiert)\b/.test(l)) {
    return true;
  }
  if (/\bnicht\s+(da|erreichbar|verfügbar|am\s+platz|dran)\b/.test(l)) return true;
  if (/\bnur\s+(die\s+)?(assistenz|sekretär|frau|herrn)\b/.test(l)) return true;
  return false;
}

/** Offline-Fallback wenn keine KI verfügbar */
export function inferCallOutcomeHeuristic(notes) {
  const t = (notes || '').trim();
  if (!t || t.length < 4) return { slug: null, confidence: 'none' };

  const l = t.toLowerCase();
  const scores = {};

  function bump(slug, n = 1) {
    if (!SLUG_SET.has(slug)) return;
    scores[slug] = (scores[slug] || 0) + n;
  }

  if (isGatekeeperContext(l)) bump('gatekeeper', 5);

  if (/\b(termin|meeting|demo)\s*(gesetzt|vereinbart|fixiert)\b/.test(l)
    || /\bsetting\s+termin\b/.test(l)
    || /\b(termin|meeting)\s+am\s+\d/.test(l)) {
    bump('reached_appt', 3);
  }
  if (/\bkein\s+interesse\b|\bnicht\s+interessiert\b|\babsage\b|\bkein\s+bedarf\b/.test(l)) bump('reached_no', 3);
  if (/\bmailbox\b|\bvoicemail\b|\bansage\b|\bauf\s+der\s+mailbox\b/.test(l)) bump('not_voicemail', 3);
  if (/\bbesetzt\b|\bbelegt\b/.test(l)) bump('not_busy', 3);
  if (/\bnicht\s+erreicht\b|\bkein\s+anschluss\b|\bniemand\s+geht\b|\bgeht\s+nicht\s+ran\b/.test(l)) bump('not_noanswer', 2);
  if (/\brückruf\b|\bzurückrufen\b|\bnochmal\s+anrufen\b|\bwieder\s+anrufen\b/.test(l)) bump('callback', 2);
  if (/\bentscheider\b|\bgeschäftsführ\b|\binhaber\b/.test(l) && /\b(direkt\s+)?(gesprochen|erreicht|im\s+gespräch)\b/.test(l)) {
    bump('decision_maker', 3);
  }
  if (/\bunterlagen\b|\bangebot\b|\bmail\s+schicken\b|\bfollow[\s-]?up\b|\bnachfassen\b/.test(l)) bump('reached_followup', 2);

  if (scores.gatekeeper >= 4) {
    delete scores.reached_appt;
    if (scores.callback) scores.callback = Math.max(0, scores.callback - 2);
  }

  const ranked = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  if (ranked.length === 0) return { slug: null, confidence: 'none' };
  const [topSlug, topScore] = ranked[0];
  const second = ranked[1]?.[1] || 0;
  if (topScore >= 3 && topScore >= second + 1) return { slug: topSlug, confidence: 'high' };
  if (topScore >= 2 && topScore > second) return { slug: topSlug, confidence: 'medium' };
  if (topScore >= 1) return { slug: topSlug, confidence: 'low' };
  return { slug: null, confidence: 'none' };
}

function normalizeAiSlug(slug) {
  if (!slug) return null;
  const s = String(slug).trim();
  return SLUG_SET.has(s) ? s : null;
}

/**
 * Ergebnis aus Notizen: zuerst KI (wenn API-Key), sonst Heuristik.
 * @returns {{ slug, confidence, source: 'ai'|'heuristic', reason? }}
 */
export async function inferCallOutcomeFromNotes(notes, { leadName, company } = {}) {
  const text = (notes || '').trim();
  if (text.length < 6) {
    return { slug: null, confidence: 'none', source: 'heuristic' };
  }

  if (isAiConfigured()) {
    try {
      const ai = await inferCallOutcomeWithAi(text, { leadName, company });
      const slug = normalizeAiSlug(ai.slug);
      if (slug) {
        return {
          slug,
          confidence: ai.confidence === 'none' ? 'medium' : ai.confidence,
          source: 'ai',
          reason: ai.reason || '',
        };
      }
      if (ai.confidence === 'low' || ai.confidence === 'none') {
        return { slug: null, confidence: 'none', source: 'ai', reason: ai.reason || '' };
      }
    } catch {
      /* Fallback auf Heuristik */
    }
  }

  const heuristic = inferCallOutcomeHeuristic(text);
  return { ...heuristic, source: 'heuristic' };
}

export { isAiConfigured as hasClaudeApiKey, isAiConfigured };
